/**
 * "Open that sheet showing only these records" — a one-shot request the sheet
 * picks up as it mounts.
 *
 * WHY A MODULE-LEVEL FLAG AND NOT AN EVENT OR A PROP.
 *
 * Exactly the shape and exactly the reason of enrichIntent.js, which does this
 * for the Fill-specs review screen. A dashboard card cannot hand a filter to a
 * sheet that is not on screen: the navigate event has to mount the tab first and
 * every sheet is a lazy chunk, so there is no delay that is both short enough to
 * feel immediate and long enough to be safe on a slow connection. A flag the
 * mounting component consumes has no race to lose.
 *
 * It is deliberately not state or context: nothing re-renders on it, and it is
 * read exactly once.
 *
 * IT IS ONE-SHOT, AND THAT IS THE PART TO PROTECT. A focus that outlived its
 * navigation would mean opening Credit from the nav bar a week later and finding
 * it silently showing four of forty rows, with the reason long forgotten. The
 * sheet takes it, clears it, and the next arrival is unfiltered.
 *
 * WHAT IT CANNOT DO, SO NOBODY GOES LOOKING: it cannot select an arbitrary set
 * of records. The payload behind these cards is capped at five items and mostly
 * carries no row identifiers at all, so there is no list of ids to filter by —
 * and even if there were, the card's `count` is the whole set while its `items`
 * are a page of it. What travels is a PREDICATE over the sheet's own columns,
 * which is why each one had to be checked against what that sheet actually
 * selects. The map lives with the cards, in dashboard/concernFocus.js.
 */

let pending = null;

/**
 * Ask for a sheet to open filtered and sorted.
 *
 * `{ sheet, filters: [{ field, filterType, lowerValue, upperValue }], sort: { field, direction } }`
 *
 * `filterType` is the vocabulary passesFilter already speaks — `=`, `!=`, `>`,
 * `>=`, `<`, `<=`, `between` for numbers and dates, substring-contains for
 * strings — so this adds no new filtering engine and inherits every rule the
 * column headers' own filter menu obeys.
 */
export function requestSheetFocus(intent) {
  pending = intent || null;
}

/**
 * Read and clear the request, if it was meant for this sheet.
 *
 * A focus meant for Credit must not be eaten by whichever sheet happens to mount
 * first — sheets load per tab, and the tab that was already open re-mounts on
 * the way past. Hence the name check before the clear rather than after.
 */
export function consumeSheetFocus(sheetName) {
  if (!pending || !sheetName || pending.sheet !== sheetName) return null;
  const was = pending;
  pending = null;
  return was;
}

/** Throw away an unclaimed focus. For tests, and for a navigation that failed. */
export function clearSheetFocus() {
  pending = null;
}
